import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash2, ExternalLink, FileText, RefreshCw, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchAdminArticles, deleteArticle, publishDraft } from "@/lib/api";

export default function AdminPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(null);

  const load = () => {
    setLoading(true);
    fetchAdminArticles().then(setPosts).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await deleteArticle(a.id);
      setPosts((p) => p.filter((x) => x.id !== a.id));
      toast.success("Post deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const publish = async (a) => {
    setPublishing(a.id);
    try {
      await publishDraft(a.id);
      toast.success(`"${a.title}" is live.`);
      load();
    } catch {
      toast.error("Publish failed.");
    } finally {
      setPublishing(null);
    }
  };

  const drafts = posts.filter((a) => a.draft);

  return (
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-16">
      <Link to="/admin/leads" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-signal transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to admin
      </Link>
      <div className="flex items-end justify-between gap-4 mt-4">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Internal</span>
          <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight mt-3 leading-none" data-testid="admin-posts-title">Manage Posts</h1>
          <p className="text-muted-foreground text-lg mt-4 max-w-3xl">
            Drafts wait here for your approval — the monthly "Biggest Movers" recap lands as a draft after every chart sync. Below that, every published story.
          </p>
        </div>
        <button data-testid="posts-refresh" onClick={load} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-white/15 px-4 py-2 hover:border-signal hover:text-signal transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {drafts.length > 0 && (
        <div className="mt-10" data-testid="drafts-section">
          <h2 className="font-anton text-2xl uppercase tracking-tight mb-3">Drafts awaiting approval ({drafts.length})</h2>
          <div className="border border-signal/40">
            {drafts.map((a) => (
              <div key={a.id} data-testid={`draft-row-${a.id}`} className="flex items-center gap-4 p-4 md:px-5 border-b border-white/10 last:border-b-0 bg-card">
                <img src={a.image} alt="" className="w-14 h-14 object-cover shrink-0 border border-white/10" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base md:text-lg font-medium leading-tight truncate">{a.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                    {a.category} · {a.auto_movers ? "Monthly Movers recap" : "Draft"} · {a.published_at ? new Date(a.published_at).toLocaleDateString() : ""}
                  </div>
                </div>
                <Link to={`/article/${a.slug}`} target="_blank" data-testid={`draft-preview-${a.id}`} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-signal shrink-0 inline-flex items-center gap-1">
                  <ExternalLink className="w-3.5 h-3.5" /> Preview
                </Link>
                <button data-testid={`draft-publish-${a.id}`} onClick={() => publish(a)} disabled={publishing === a.id} className="inline-flex items-center gap-2 bg-signal text-white font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:bg-signal-hover transition-colors disabled:opacity-50 shrink-0">
                  {publishing === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Publish
                </button>
                <button data-testid={`post-delete-${a.id}`} onClick={() => remove(a)} className="text-muted-foreground hover:text-signal shrink-0" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <p className="font-mono text-sm text-muted-foreground mt-10">Loading…</p>
      ) : posts.length === 0 ? (
        <div className="border border-white/10 bg-card p-10 text-center mt-10">
          <FileText className="w-8 h-8 text-muted-foreground mx-auto" />
          <p className="font-mono text-sm text-muted-foreground mt-3">No posts yet.</p>
        </div>
      ) : (
        <div className="border-x border-t border-white/10 mt-10">
          {posts.filter((a) => !a.draft).map((a) => (
            <div key={a.id} data-testid={`post-row-${a.id}`} className="flex items-center gap-4 p-4 md:px-5 border-b border-white/10 hover:bg-secondary transition-colors">
              <img src={a.image} alt="" className="w-14 h-14 object-cover shrink-0 border border-white/10" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-base md:text-lg font-medium leading-tight truncate">{a.title}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                  {a.category} · {a.author} · {a.ai_generated ? "AI" : "Seed"} · {a.published_at ? new Date(a.published_at).toLocaleDateString() : ""}
                </div>
              </div>
              <Link to={`/article/${a.slug}`} target="_blank" className="text-muted-foreground hover:text-signal shrink-0" aria-label="View"><ExternalLink className="w-4 h-4" /></Link>
              <button data-testid={`post-delete-${a.id}`} onClick={() => remove(a)} className="text-muted-foreground hover:text-signal shrink-0" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
